/* globals localStorage FS fetch window */
import React from 'react';
import moment from 'moment';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import qstring from 'query-string';
import { FullStory } from '@fullstory/browser';
import { setFSCustomEvent } from './fullstory';
import { apiGet, getBrain2Url, getProNameHeader } from './brainApi';

// Some basic abstract helper functions

export function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}

// A string-comparison method to be used when sorting
export function compareStrings(a, b, reverseAlphabetical = false) {
  const multiplier = reverseAlphabetical ? -1 : 1;
  if (a < b) {
    return -1 * multiplier;
  }
  if (a > b) {
    return 1 * multiplier;
  }
  return 0;
}

export function roundToTwoDecimals(number) {
  return Math.round(number * 100) / 100.0;
}

export function formatPrice(amountDollars) {
  if (amountDollars === null || amountDollars === undefined) {
    return '';
  }
  // Try to format with 0 decimals (as long as it's a round number)
  if (Math.round(amountDollars) === amountDollars) {
    return `$${amountDollars.toFixed(0)}`;
  }
  // Otherwise, format with 2 decimals
  return `$${amountDollars.toFixed(2)}`;
}

// Return a range of dates between (and including) two given dates
export function getDateArray(startDate, stopDate, includeWeekends = true) {
  const dateArray = [];
  const currentDate = new Date(startDate);
  while (currentDate <= new Date(stopDate)) {
    // if we're including weekends or if this day is not a saturday or sunday
    if (includeWeekends || ![0, 6].includes(currentDate.getDay())) {
      // add this date to our array
      dateArray.push(new Date(currentDate));
    }
    // move on to the next date
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dateArray;
}

export function employeeAvatarImg(imageUrl, size, facepad, extraStyle = {}) {
  if (!imageUrl) {
    return undefined;
  }
  const fullUrl = `${imageUrl}?fit=facearea&facepad=${facepad}&mask=ellipse&w=${size}&h=${size}&auto=format`;
  return (
    <img
      src={fullUrl}
      style={{
        width: size,
        height: size,
        verticalAlign: 'middle',
        borderRadius: '50%',
        ...extraStyle,
      }}
      alt="Employee"
    />
  );
}

export function employeeAvatarLink(imageUrl, width = 40) {
  if (!imageUrl) {
    return (
      <AccountCircleIcon
        style={{ width, fontSize: width, verticalAlign: 'middle' }}
      />
    );
  }

  return (
    <a href={imageUrl} target="_blank" rel="noopener noreferrer">
      {employeeAvatarImg(imageUrl, width, 1.6)}
    </a>
  );
}

export function localStorageGet(item) {
  const user = JSON.parse(localStorage.getItem('user'));
  switch (item) {
    case 'user':
      return user || null;
    case 'token':
      return user?.token || localStorage.getItem('token') || null;
    case 'username':
      return user?.username || localStorage.getItem('username') || null;
    default:
      return localStorage.getItem(item);
  }
}

export function getCurrentEmployeeImageUrl() {
  const employee = localStorage.getItem('employee');
  if (employee) {
    const employeeJson = JSON.parse(employee);
    return employeeJson ? employeeJson.avatar_image_url : '';
  }
  return '';
}

export function sendToFullStory(user) {
  if (user) {
    try {
      const userObj = typeof user === 'string' ? JSON.parse(user) : user;
      const { first_name, last_name, username } = userObj;
      if (username) {
        if (FullStory) {
          // Use modern identify API
          FullStory('identify', {
            uid: username,
            displayName: `${first_name || ''} ${last_name || ''}`.trim() || username,
            email: username,
          });
        }
      }
    } catch {
      console.error('Failed to sendToFullStory');
    }
  }
}

export function trackFullStoryEvent(eventName, properties = {}) {
  try {
    console.log('[FullStory Event Captured]', eventName, properties);
    if (typeof window !== 'undefined' && window.FS && typeof window.FS.event === 'function') {
      window.FS.event(eventName, properties);
    } else {
      setFSCustomEvent(eventName, properties);
    }
  } catch (err) {
    console.error(`Failed to track FullStory event: ${eventName}`, err);
  }
}

export function userIsZippityEmployee() {
  const user = JSON.parse(localStorage.getItem('user'));
  return (
    user?.username?.endsWith('@getzippity.com') ||
    user?.username?.endsWith('@zippitycars.com') ||
    user?.username?.endsWith('@zippityinfra.com') ||
    user?.username?.endsWith('@simpletire.com')
  );
}

export function getLocalStorage(name) {
  return localStorage.getItem(name);
}

export function setLocalStorage(name, value) {
  return localStorage.setItem(name, value);
}

export function formatHumanReadableDate(date) {
  const dateMoment = moment(date);
  const isCurrentYear = moment().isSame(dateMoment, 'year');
  if (isCurrentYear) {
    return dateMoment.format('MMM D');
  }
  return dateMoment.format('MMM D, YYYY');
}

export const timelineLimit = 10;
export const perPage = 200;

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

export function pickColor(str) {
  return `hsl(${hashCode(str) % 360}, 100%, 40%)`;
}

export function getZ3PConfigValue(name = null) {
  const config = localStorage.getItem('z3pConfiguration');
  const parsedConfig = config ? JSON.parse(config) : {};
  if (!name) {
    return parsedConfig;
  }
  return parsedConfig[name];
}

export function areTimeSlotsEnabled() {
  return getZ3PConfigValue('scheduling_system') === 'time_slots';
}

export function encodeParams(params) {
  const ret = [];
  Object.keys(params).forEach((k) => {
    const key = encodeURIComponent(k);
    const val = encodeURIComponent(params[k]);
    ret.push(`${key}=${val}`);
  });
  return ret.join('&');
}

export function emailValidationRegExp() {
  return new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
}

export function convertPhoneFormat(phone) {
  const cleanedPhone = `${phone}`.replace(/\D/g, '');
  const match = cleanedPhone.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const number = `(${match[2]}) ${match[3]}-${match[4]}`;
    return number;
  }
  return phone;
}

export function convertMilitaryNumberToAmPm(time) {
  // Conversions are only to the half hour (:00 or :30)
  const hour = Math.floor(time);
  const min = time % 1 === 0 ? '00' : '30';
  const ampm = hour < 12 ? 'am' : 'pm';
  const hour12 = ((hour - 1) % 12) + 1;
  return `${hour12}:${min} ${ampm}`;
}

export function convertMilitaryNumberToMilitaryString(time) {
  // Conversions are only to the half hour (:00 or :30)
  if (time % 1 !== 0) {
    return time < 10
      ? `0${time.toString().split('.')[0]}:30`
      : `${time.toString().split('.')[0]}:30`;
  }
  return time < 10 ? `0${time.toString()}:00` : `${time.toString()}:00`;
}

export function convertAmPmToMilitaryString(time) {
  // Conversions are only to the half hour (:00 or :30)
  let [hour, minute, ampm] = time.split(/:| /);
  hour = Number(hour);
  if (hour === 12) {
    hour -= 12;
  }
  if (ampm === 'PM') {
    hour += 12;
  }
  if (minute === '30') {
    return hour < 10 ? `0${hour}:30` : `${hour}:30`;
  }
  return hour < 10 ? `0${hour}:00` : `${hour}:00`;
}

export function convertAmPmToMilitaryNumber(time) {
  // Conversions are only to the half hour (:00 or :30)
  let [hour, minute, ampm] = time.split(/:| /);
  hour = Number(hour);
  if (hour === 12) {
    hour -= 12;
  }
  if (ampm === 'PM') {
    hour += 12;
  }
  if (minute === '30') {
    hour += 0.5;
  }
  return hour;
}

export function convertMilitaryStringToMilitaryNumber(time) {
  // Conversions are only to the half hour (:00 or :30)
  const [hour, minute] = time.split(':');
  if (minute === '30') {
    // Convert to Number to remove the leading 0
    return Number(`${hour}.5`);
  }
  return Number(hour);
}

export function convertMilitaryStringToAmPm(time) {
  let [hour, minute] = time.split(':');
  hour = Number(hour);
  let ampm = 'AM';
  if (Number(hour) === 0) {
    hour = 12;
  }
  if (Number(hour) >= 12) {
    ampm = 'PM';
    if (Number(hour) > 12) {
      hour = Number(hour) - 12;
    }
  }
  return `${hour}:${minute} ${ampm}`;
}

export function convertTime(time, outputFormat) {
  // This function accepts a time in any of the following formats: "3:30 PM", "15:30", 15.5
  // Based on the outputFormat, convert to any of the same 3 formats (ampm, militaryString, militaryNumber)
  // Conversions are only to the half hour (:00 or :30)
  const regexpAmPm = new RegExp('AM|PM');
  const regexpString = new RegExp('.{2}:.{2}');
  if (!time && time !== 0) {
    return null;
  }
  if (regexpAmPm.test(time)) {
    if (outputFormat === 'ampm') {
      return time;
    }
    if (outputFormat === 'militaryString') {
      // Convert "2:30 PM" to "14:30"
      return convertAmPmToMilitaryString(time);
    }
    if (outputFormat === 'militaryNumber') {
      // Convert 8:00 AM to 8 and 2:30 PM to 14.5
      return convertAmPmToMilitaryNumber(time);
    }
  } else if (regexpString.test(time)) {
    if (outputFormat === 'ampm') {
      // Convert "14:30" to "2:30 PM"
      return convertMilitaryStringToAmPm(time);
    }
    if (outputFormat === 'militaryString') {
      return time;
    }
    if (outputFormat === 'militaryNumber') {
      // Convert "14:30" to 14.5 or "08:00" to 8
      return convertMilitaryStringToMilitaryNumber(time);
    }
  } else {
    // Assume the time received is a number
    if (outputFormat === 'ampm') {
      // Convert 14.5 to "2:30 PM"
      return convertMilitaryNumberToAmPm(Number(time));
    }
    if (outputFormat === 'militaryString') {
      // Convert 14.5 to "14:30" or 8 to "08:00"
      return convertMilitaryNumberToMilitaryString(Number(time));
    }
    if (outputFormat === 'militaryNumber') {
      return Number(time);
    }
  }
  return time;
}

// Take a UTC time and format it in the user's local timezone
export function dateTimeFormat(date) {
  return moment
    .utc(date)
    .local()
    .format('MM/DD/YYYY hh:mm:ss A');
}

// Sort an array of obj based on the key passed
export function sortByKey(a, b, key) {
  if (a[key] < b[key]) {
    return -1;
  }
  if (a[key] > b[key]) {
    return 1;
  }
  return 0;
}

export function shouldShowImportButton({ history }) {
  // If the user added "?showImportButton=true" to the URL, then show the import button.
  const params = qstring.parse(history.location.search, {
    parseBooleans: true,
  });
  return !!params.showImportButton;
}

export function shouldAllowMultipleScheduleTypes({ history }) {
  // If the user added "?shouldAllowMultipleScheduleTypes=true" to the URL, then allow multiple schedule type selection in service catalog.
  const params = qstring.parse(history.location.search, {
    parseBooleans: true,
  });
  return !!params.shouldAllowMultipleScheduleTypes;
}

// Accepts a time with format "15:30:55" and converts it to "3:30 pm"
export function convertMilitaryStringWithSecondsToAmPm(time) {
  return moment(time, 'HH:mm:ss').format('h:mm a');
}

export function daysBetween(date1, date2) {
  if (!date1 || !date2) return null;
  const momentDate1 = moment(date1);
  const momentDate2 = moment(date2);
  return momentDate1.diff(momentDate2, 'days');
}

export function formatAlphanumericWithSpaces(str) {
  return str.replace(/\W+/g, ' ');
}

export function filenameToExtension(filename) {
  const suffix = filename
    .split('.')
    .pop()
    .toLowerCase();
  if (suffix === 'jpg') {
    return 'jpeg';
  }
  return suffix;
}

// Ex. "pickup_and_delivery" to "Pickup and delivery"
export function humanizeString(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}

export function exclusiveOR(x, y) {
  return (x || y) && !(x && y);
}

export function stringIsInt(str) {
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(str) && !isNaN(parseInt(str));
}

// https://stackoverflow.com/questions/12374442/chrome-ignores-autocomplete-off
// The value should be any string other than the allowed values here:
// https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
// Used for antd Inputs inside AutoComplete: https://stackoverflow.com/a/56258877
// And Material UI TextFields: https://material-ui.com/api/text-field/
export const disableAutofill = 'dontautofill';

// And here's one for react-admin AutocompleteInput: https://github.com/marmelab/react-admin/issues/5620
export const disableAutofillOptions = {
  InputProps: { autoComplete: 'dontautofill' },
};

// Determines what string to display for service vehicles and/or service bays and how to capitalize
export const getServiceEntity = (plural = false, letterCase = 'title') => {
  const serviceLocations = getZ3PConfigValue('service_locations');
  const hasShopServices = serviceLocations?.includes('shop');
  if (serviceLocations?.length > 1 && hasShopServices) {
    switch (letterCase) {
      case 'capitalization':
        return `Service vehicle${plural ? 's' : ''}/bay${plural ? 's' : ''}`;
      case 'lower':
        return `service vehicle${plural ? 's' : ''}/bay${plural ? 's' : ''}`;
      default:
        return `Service Vehicle${plural ? 's' : ''}/Bay${plural ? 's' : ''}`;
    }
  }
  if (serviceLocations?.length === 1 && hasShopServices) {
    switch (letterCase) {
      case 'capitalization':
        return `Service bay${plural ? 's' : ''}`;
      case 'lower':
        return `service bay${plural ? 's' : ''}`;
      default:
        return `Service Bay${plural ? 's' : ''}`;
    }
  }
  switch (letterCase) {
    case 'capitalization':
      return `Service vehicle${plural ? 's' : ''}`;
    case 'lower':
      return `service vehicle${plural ? 's' : ''}`;
    default:
      return `Service Vehicle${plural ? 's' : ''}`;
  }
};

export const uploadImage = (folder, file) => {
  const signingUrl = `${getBrain2Url()}/util/s3-sign/${folder}`;
  const stage = process.env.REACT_APP_DATA_PROVIDER || '';
  const currentTimestamp = Date.now();
  const extension = filenameToExtension(file.name);
  const filename = `${stage}-${currentTimestamp}-${file.name.replace(
    /\s/g,
    '_',
  )}`;

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Pro-Name': getProNameHeader(),
  };

  // Step 1: Call Brain2 to get a signed S3 URL
  return fetch(`${signingUrl}?objectName=${filename}&contentType=image/*`, {
    headers,
  })
    .then((res) => res.json())
    .then((res) => {
      const { signedUrl } = res;
      if (signedUrl) {
        // Step 2: Use the signed URL to upload the image to S3
        return fetch(signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': `image/${extension}` },
          body: file,
        }).then((data) => {
          if (data.status === 200) {
            return filename;
          }
          console.error(data);
          return null;
        });
      }
      console.error(res);
      return null;
    });
};

export const formatScheduleTypeName = (name, translate) => {
  let displayName = translate(`resources.schedule-type.display.${name}`);
  if (displayName.includes('resources')) {
    displayName = humanizeString(name);
  }
  return displayName;
};

export const possibleProStatuses = {
  active: 'Active',
  churned: 'Churned',
  delinquent: 'Delinquent',
  paused: 'Paused',
};

export const formatAppointmentTime = (sw) => {
  const {
    confirmed_appointment_start_time = null,
    planned_arrival_time = null,
    planned_departure_time = null,
  } = sw;

  let timeString = 'Time not set';
  // if (planned_arrival_time) {
  //   timeString = convertMilitaryStringToAmPm(confirmed_appointment_start_time);
  // } else
  if (planned_arrival_time && planned_departure_time) {
    const arrival = convertMilitaryStringToAmPm(planned_arrival_time);
    const departure = convertMilitaryStringToAmPm(planned_departure_time);
    timeString = `${arrival} - ${departure}`;
  }
  return timeString;
};

// This function tokenizes a Square card.
// The ‘error’ thrown from this async function denotes a failed tokenization,
// which is due to buyer error (such as an expired card). It is up to the
// developer to handle the error and provide the buyer the chance to fix
// their mistakes.
export const tokenize = async (card_) => {
  const tokenResult = await card_.tokenize();
  if (tokenResult.status === 'OK') {
    return tokenResult.token;
  }
  let errorMsg = `Tokenization failed-status: ${tokenResult.status}`;
  if (tokenResult.errors) {
    errorMsg += ` and errors: ${JSON.stringify(tokenResult.errors)}`;
  }
  throw new Error(errorMsg);
};

export const authorizeGoogle = async (onSuccess, overrideToken = null) => {
  const res = await apiGet('/integration/google/request-auth', {
    override_token: overrideToken,
  });

  if (res.auth_url) {
    window.open(res.auth_url, '_blank', 'popup=true');
  }
};

export const storeZ3PConfiguration = (
  z3pConfiguration,
  shouldReload = false,
) => {
  const currentZ3PConfig = localStorage.getItem('z3pConfiguration');

  if (currentZ3PConfig !== JSON.stringify(z3pConfiguration) || shouldReload) {
    localStorage.setItem('z3pConfiguration', JSON.stringify(z3pConfiguration));
    window.location.reload();
  }
};

export function tracRudderStackEvent(eventName, properties = {}) {
  if (window && window.rudderanalytics) {
    window.rudderanalytics.track(eventName, properties);
  } else {
    console.warn('rudderanalytics not initialized yet');
  }
}

export const currentPageInfo = {
  URL: window.location.href,
  PATH: window.location.pathname,
  TITLE: document.title,
};
